import { onBeforeUnmount, reactive } from 'vue';
import { useToast } from '@/composables/useToast';
import { dashboardSubscriptionService } from '@/services/DashboardSubscriptionService';
import { DashboardSubscription, SubscriptionRun } from '@/types/DashboardSubscription';

export const SEND_NOW_POLL_MS = 3000;
export const SEND_NOW_POLL_LIMIT_MS = 180000;
export const SEND_NOW_COOLDOWN_MS = 60000;

export function useSendNow(onSettled: (subscriptionId: number) => Promise<void> | void) {
  const toast = useToast();
  const sending = reactive(new Set<number>());
  const coolingDown = reactive(new Set<number>());
  const cooldownTimers = new Map<number, ReturnType<typeof setTimeout>>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let stopped = false;

  function later(action: () => void | Promise<void>, delayMs: number) {
    const timer = setTimeout(() => {
      timers.delete(timer);
      action();
    }, delayMs);
    timers.add(timer);
    return timer;
  }

  function startCooldown(subscriptionId: number) {
    endCooldown(subscriptionId);
    coolingDown.add(subscriptionId);
    cooldownTimers.set(
      subscriptionId,
      later(() => endCooldown(subscriptionId), SEND_NOW_COOLDOWN_MS),
    );
  }

  function endCooldown(subscriptionId: number) {
    const timer = cooldownTimers.get(subscriptionId);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(timer);
      cooldownTimers.delete(subscriptionId);
    }
    coolingDown.delete(subscriptionId);
  }

  async function settle(subscriptionId: number) {
    sending.delete(subscriptionId);
    await onSettled(subscriptionId);
  }

  async function poll(subscriptionId: number, runId: number, recipient: string, elapsedMs: number) {
    let run: SubscriptionRun | undefined;
    try {
      run = await dashboardSubscriptionService.run(subscriptionId, runId);
    } catch {
      // A failed status check says nothing about the send, so it is retried like a run still in flight.
    }
    if (stopped) {
      return;
    }
    if (run?.result === 'DELIVERED') {
      toast.success(`Sent to ${recipient}.`);
    } else if (run?.result === 'FAILED') {
      // Nothing was sent, and the API's cooldown ignores failed and skipped runs too.
      endCooldown(subscriptionId);
      toast.error(run.message);
    } else if (run?.result === 'SKIPPED') {
      endCooldown(subscriptionId);
      toast.warning(run.message);
    } else if (elapsedMs < SEND_NOW_POLL_LIMIT_MS) {
      later(() => poll(subscriptionId, runId, recipient, elapsedMs + SEND_NOW_POLL_MS), SEND_NOW_POLL_MS);
      return;
    } else {
      toast.info('Still sending. Check History for the result.');
    }
    await settle(subscriptionId);
  }

  async function send(subscription: DashboardSubscription): Promise<void> {
    const id = subscription.id;
    if (sending.has(id)) {
      return;
    }
    sending.add(id);
    try {
      const started = await dashboardSubscriptionService.sendNow(id);
      startCooldown(id);
      later(() => poll(id, started.id, started.recipient, SEND_NOW_POLL_MS), SEND_NOW_POLL_MS);
    } catch (error) {
      sending.delete(id);
      toast.error(error instanceof Error ? error.message : 'The dashboard could not be sent.');
    }
  }

  onBeforeUnmount(() => {
    stopped = true;
    timers.forEach(clearTimeout);
    timers.clear();
  });

  return {
    send,
    isSending: (id: number) => sending.has(id),
    isCoolingDown: (id: number) => coolingDown.has(id),
  };
}
